/**
 * Option strategies on screen.
 *
 * The server names option contracts (`optionLabel`, "AAPL 200 call · Jan 16 '26")
 * and tags legs placed together with one `strategy` ({ id, name, label }) —
 * see server/utils/optionStrategies.js. This turns those legs into one row
 * per strategy for trade and position lists. Totals are unchanged: the row's
 * P&L is the sum of its legs.
 */

const legText = (r) => {
  const side = String(r.side || '').toLowerCase().startsWith('s') ? 'Short' : 'Long';
  const qty = Math.abs(Number(r.quantity) || 0);
  if (r.optionLabel) return `${side} ${qty > 1 ? `${qty}× ` : ''}${r.optionLabel.replace(/ · .*$/, '').replace(/^\S+ /, '')}`;
  return `${side} ${qty.toLocaleString('en-US')} shares`;
};

const sum = (rows, key) => {
  const vals = rows.map((r) => r[key]).filter((v) => v != null && Number.isFinite(Number(v)));
  return vals.length ? vals.reduce((s, v) => s + Number(v), 0) : null;
};

/**
 * Rows → display rows. Legs sharing a strategy (per account) become one row:
 *   symbol: the strategy label, sideLabel: its name, legs: what's in it,
 *   profit / unrealizedPl / marketValue: summed.
 * A lone option keeps its row with a readable symbol and "Short call"-style sideLabel.
 */
export function collapseStrategies(rows = []) {
  const groups = new Map();
  const out = [];
  for (const r of rows || []) {
    if (r?.isStrategy || r?.rawSymbol) {
      out.push(r); // already collapsed
    } else if (r?.strategy?.id) {
      const key = `${r.account || ''}|${r.strategy.id}`;
      let g = groups.get(key);
      if (!g) {
        g = { first: r, rows: [] };
        groups.set(key, g);
        out.push(g);
      }
      g.rows.push(r);
    } else if (r?.optionLabel) {
      out.push({ ...r, symbol: r.optionLabel, sideLabel: r.optionStrategy || null, rawSymbol: r.symbol });
    } else {
      out.push(r);
    }
  }
  return out.map((item) => {
    if (!item.rows) return item;
    const { first, rows: legs } = item;
    const dates = (key) => legs.map((l) => l[key]).filter(Boolean).sort();
    const entries = dates('entryDate');
    const exits = dates('exitDate');
    return {
      ...first,
      symbol: first.strategy.label,
      sideLabel: first.strategy.name,
      isStrategy: true,
      side: null,
      quantity: null,
      entryPrice: null,
      exitPrice: null,
      currentPrice: null,
      avgEntryPrice: null,
      entryDate: entries[0] || first.entryDate,
      exitDate: exits[exits.length - 1] || first.exitDate,
      profit: sum(legs, 'profit'),
      roi: null,
      unrealizedPl: sum(legs, 'unrealizedPl'),
      unrealizedPlpc: null,
      marketValue: sum(legs, 'marketValue'),
      legs: legs.map((l) => ({ text: legText(l), profit: l.profit ?? l.unrealizedPl ?? null })),
      legsText: [...new Set(legs.map(legText))].join(', '),
    };
  });
}

/** The side tag text: the strategy or option name, else Long / Short. */
export function sideTagText(t) {
  if (t?.sideLabel) return t.sideLabel;
  const side = String(t?.side || '').toLowerCase();
  if (!side) return null;
  return side.startsWith('s') ? 'Short' : 'Long';
}

/** CSS modifier for the side tag. */
export function sideTagClass(t) {
  if (t?.isStrategy) return 'is-strategy';
  const side = String(t?.side || '').toLowerCase();
  return side.startsWith('s') ? 'is-short' : 'is-long';
}
